import { useCallback } from 'react';
import { css } from '@emotion/react';
import { Status_Enum } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { PersonTypes } from '@frontend/api-person';
import { AppointmentStatusComponents } from '@frontend/appointment-status';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { ContactDetailsItem } from '../../../contact-details/contact-details-item';
import { CustomerDetailsProps } from '../../customer-details';
import { AppointmentDetails } from './appointment-details-card';

const { StatusChip } = AppointmentStatusComponents;

dayjs.extend(utc);
dayjs.extend(minMax);

interface PreparedAppointments {
  due: DueAppointment;
  last: PreparedAppointment;
  next: PreparedAppointment;
}

interface DueAppointment {
  data: string | null;
  label: string | Date;
}
interface PreparedAppointment {
  data: PersonTypes.Appointment | null;
  label: string | Date;
}

interface AppointmentInformationProps {
  hasRecallIntegration: boolean;
  locationId: string;
}

export const AppointmentInformation = ({
  data,
  locationId,
  hasRecallIntegration,
}: CustomerDetailsProps & AppointmentInformationProps) => {
  const { t } = useTranslation('schedule');

  const person = data;

  const findAppointment = (searchParam: string): PersonTypes.Appointment | null => {
    const today = dayjs();
    const appointments = person?.Appointments;
    const searchLast = searchParam === 'last';
    const filteredAppointments = appointments?.filter((appointment) => {
      const appointmentDate = dayjs(appointment.AppointmentDate);
      return searchLast ? appointmentDate < today : appointmentDate > today;
    });

    if (filteredAppointments?.length) {
      const appointmentIndex = searchLast ? 0 : filteredAppointments.length - 1;
      return filteredAppointments[appointmentIndex];
    }

    return null;
  };

  const prepareAppointments = useCallback((): PreparedAppointments => {
    const appointmentExists = !!person?.Appointments;
    const lastAppointment = appointmentExists ? findAppointment('last') : null;
    const nextAppointment = appointmentExists ? findAppointment('next') : null;
    const recalls = person?.Recall ?? [];
    const dueDate = recalls.map((recall) => {
      return dayjs.utc(recall.EventTime);
    });
    const dueAppointment = recalls.length ? dayjs.min(dueDate).format('MMMM DD, YYYY') : null;

    return {
      due: {
        data: dueAppointment,
        label: dueAppointment ? dueAppointment : 'None',
      },
      last: {
        data: lastAppointment,
        label: lastAppointment ? lastAppointment.AppointmentDate : 'None',
      },
      next: {
        data: nextAppointment,
        label: nextAppointment ? nextAppointment.AppointmentDate : 'None',
      },
    };
  }, [person]);

  const { last, next, due } = prepareAppointments();

  if (!person) return null;

  return (
    <section css={{ padding: theme.spacing(2) }}>
      <div css={headingStyles}>
        <Heading level={3}>{t('Appointment Status')}</Heading>
        {next?.data && typeof next.data !== 'string' && next?.data?.Status && (
          <StatusChip status={next.data.Status.toUpperCase() as Status_Enum} />
        )}
      </div>
      {next?.data ? (
        <AppointmentDetails appointment={next?.data && next} locationId={locationId} />
      ) : (
        <>
          <ContactDetailsItem title={'Next Appointment'} value={'-'} />
        </>
      )}
      <section css={detailsItemStyles}>
        <ContactDetailsItem
          title={'Due'}
          value={due.data ? dayjs(due.label).format('MMMM D, YYYY') : '-'}
          omit={!hasRecallIntegration}
          css={css({
            p: { textWrap: 'wrap' },
          })}
        />
        <ContactDetailsItem
          title={'Last Appointment'}
          value={last.data ? dayjs(last.label).format('MMMM D, YYYY') : '-'}
          css={css({
            p: { textWrap: 'wrap' },
            span: { marginRight: theme.spacing(1) },
          })}
        />
      </section>
    </section>
  );
};

const headingStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
});

const detailsItemStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(3),
  gap: theme.spacing(2),
});
